import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminNews } from "@/modules/news/admin/types";

export type NewsList = Paginate<AdminNews<true>>;

export const news = createQueryKeys("admin-news", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "categories";

export const fetchEntriesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...news.all(page, search),
    queryFn: () =>
      adminApi
        .get<NewsList>(`/news`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchEntries = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchEntriesQueryOptions(page, search));

export const fetchNewsQueryOptions = (id?: number) =>
  queryOptions({
    ...news.one(id),
    queryFn: () =>
      adminApi
        .get<AdminNews<true>>(`/news/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchNews = (id?: number) =>
  useQuery(fetchNewsQueryOptions(id));

export interface NewsRequest {
  title: string;
  excerpt: string;
  content: string;
  status: "draft" | "published" | "disabled";
  featured_image: string | null;
  image_thumb?: string | null;
  categories: number[] | string[];
  allow_comments?: boolean;
  is_pinned?: boolean;
  is_author_hidden?: boolean;
  live_update_status?: "active" | "ended" | null;
  live_update_label?: string | null;
  bump_to_top?: boolean;
  archive_at?: string | null;
}

export const useCreateNews = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: NewsRequest) =>
      adminApi.post(`/news/`, data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
    },
  });
};

export const useUpdateNews = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: NewsRequest) =>
      adminApi.put(`/news/${id}`, data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};

export const useCreateOrUpdateNews = (id: number | undefined) => {
  const create = useCreateNews();
  const update = useUpdateNews(id!);
  return id ? update : create;
};

export const useDeleteNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`news/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};

export const useUpdateNewsStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: AdminNews["status"] }) =>
      adminApi
        .patch(`/news/${id}/status`, { status })
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};

export const usePinNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      adminApi.put(`/news/${id}/pin`).then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};

export const useUnPinNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      adminApi.delete(`/news/${id}/pin`).then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};
